import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Flame, CheckCircle2, Loader2 } from "lucide-react";

const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 12;

const GiveSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState({ phase: "checking", payment_status: null, amount: null });
  const polled = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }
    const poll = async () => {
      try {
        const r = await api.get(`/donations/status/${sessionId}`);
        const ps = r.data.payment_status;
        if (ps === "paid") {
          setStatus({ phase: "paid", payment_status: ps, amount: r.data.amount_usd });
          return;
        }
        if (r.data.status === "expired" || ps === "expired") {
          setStatus({ phase: "expired", payment_status: ps, amount: r.data.amount_usd });
          return;
        }
        polled.current += 1;
        if (polled.current >= MAX_POLLS) {
          setStatus({ phase: "timeout", payment_status: ps, amount: r.data.amount_usd });
          return;
        }
        timer.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setStatus({ phase: "error", payment_status: null, amount: null });
      }
    };
    poll();
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [sessionId, navigate]);

  const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="min-h-screen flex items-center justify-center ember-bg px-6">
      <div className="bento-card !p-10 max-w-xl w-full text-center" data-testid="give-success">
        <span className="glow-blob" />
        <div className="icon-badge !h-14 !w-14 mx-auto mb-6">
          {status.phase === "paid" ? <CheckCircle2 className="h-7 w-7 text-[#C9A44C]" strokeWidth={1.5} /> : status.phase === "checking" ? <Loader2 className="h-7 w-7 text-[#C9A44C] animate-spin" /> : <Flame className="h-7 w-7 text-[#F28C18]" />}
        </div>
        {status.phase === "paid" ? (
          <>
            <div className="overline mb-2">Partner with the Fire</div>
            <h1 className="font-serif-display text-4xl text-[#F6F0E6]">Thank you.</h1>
            <p className="mt-3 text-[#D9D9D9]">Your gift of <span className="text-[#C9A44C]">{fmt(status.amount)}</span> has been received. We are honoured to stand with you.</p>
            <p className="mt-2 font-serif-display italic text-[#C9A44C]">Come and meet Jesus. Carry the fire.</p>
          </>
        ) : status.phase === "checking" ? (
          <>
            <div className="overline mb-2">Finalising</div>
            <h1 className="font-serif-display text-3xl text-[#F6F0E6]">Confirming your gift…</h1>
            <p className="mt-3 text-[#D9D9D9]/70 text-sm">This usually takes a few seconds.</p>
          </>
        ) : status.phase === "expired" ? (
          <>
            <h1 className="font-serif-display text-3xl text-[#F6F0E6]">Session expired</h1>
            <p className="mt-3 text-[#D9D9D9]">Your checkout session expired. Please try again.</p>
          </>
        ) : status.phase === "timeout" ? (
          <>
            <h1 className="font-serif-display text-3xl text-[#F6F0E6]">Still confirming</h1>
            <p className="mt-3 text-[#D9D9D9]">We&rsquo;re still verifying with the bank. You&rsquo;ll receive an email if it completes.</p>
          </>
        ) : (
          <>
            <h1 className="font-serif-display text-3xl text-[#F6F0E6]">Something went wrong</h1>
            <p className="mt-3 text-[#D9D9D9]">Please try again or contact rfmgcontact@gmail.com.</p>
          </>
        )}
        <a href="/" className="btn-ghost mt-8 inline-flex" data-testid="give-success-home">Return Home</a>
      </div>
    </div>
  );
};

export default GiveSuccess;
